import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  Wifi, 
  BarChart3, 
  Target, 
  FileDown,
  Info,
  CheckCircle2,
  Circle,
  Clock
} from 'lucide-react';
import { ShotVisualizationSystem } from '@/features/shot-visualization/components/ShotVisualizationSystem';
import { LiveMonitor } from '@/features/shot-visualization/components/LiveMonitor';
import { EnhancedAnalyticsDashboard } from '@/features/shot-visualization/components/EnhancedAnalyticsDashboard';

// Mock session ID for demo
const DEMO_SESSION_ID = 'session-001';

export const ShotVisualizationDemo: React.FC = () => {
  const [activeFeature, setActiveFeature] = useState<string>('overview');

  const features = [
    {
      id: 'api-integration',
      title: 'API Integration',
      status: 'completed',
      description: 'Connect visualization to existing database',
      icon: Database,
      details: [
        'React Query hooks for data fetching',
        'Automatic caching and invalidation',
        'Error handling and retry logic',
        'Support for session and player data'
      ]
    },
    {
      id: 'live-mode',
      title: 'Live Mode',
      status: 'completed',
      description: 'Real-time shot updates via WebSocket',
      icon: Wifi,
      details: [
        'WebSocket connection for instant updates',
        'Live shot feed with real-time visualization',
        'Session statistics and monitoring',
        'Shot simulation for testing'
      ]
    },
    {
      id: 'analytics-view',
      title: 'Analytics View',
      status: 'completed',
      description: 'Charts showing shot patterns and trends',
      icon: BarChart3,
      details: [
        'Shot distribution wagon wheel',
        'Performance trends over time',
        'Quality metrics visualization',
        'Comparative analysis charts'
      ]
    },
    {
      id: 'comparison-tool',
      title: 'Comparison Tool',
      status: 'pending',
      description: 'Multi-shot overlay visualization',
      icon: Target,
      details: [
        'Side-by-side trajectory comparison',
        'Overlay multiple shots',
        'Highlight differences',
        'Best vs actual comparison'
      ]
    },
    {
      id: 'export-reports',
      title: 'Export/Reports',
      status: 'pending',
      description: 'Generate PDF reports with visualizations',
      icon: FileDown,
      details: [
        'PDF report generation',
        'Include 3D visualizations',
        'Session summaries',
        'Player progress reports'
      ]
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-blue-600 animate-pulse" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'in-progress':
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      default:
        return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">Data-Driven Shot Visualization</h1>
        <p className="text-xl text-gray-600">
          Experience the power of real-time cricket shot analysis with database integration
        </p>
      </div>

      {/* Feature Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Implementation Progress</CardTitle>
          <CardDescription>
            Track the development of data-driven features for the shot visualization system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={feature.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    activeFeature === feature.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => setActiveFeature(feature.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="h-8 w-8 text-blue-600" />
                        <div>
                          <CardTitle className="text-lg">{feature.title}</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      {getStatusIcon(feature.status)}
                      {getStatusBadge(feature.status)}
                    </div>
                    <ul className="mt-3 space-y-1">
                      {feature.details.map((detail, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="text-blue-600 mt-1">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Alert for Completed Features */}
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertTitle>New Feature Complete: Analytics Dashboard!</AlertTitle>
        <AlertDescription>
          The shot visualization system now includes comprehensive analytics:
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li><strong>Wagon Wheel:</strong> Cricket field shot distribution visualization</li>
            <li><strong>Historical Trends:</strong> Time-series performance analysis with Recharts</li>
            <li><strong>Enhanced Dashboard:</strong> Tabbed analytics with filtering and export</li>
            <li><strong>Live Data Integration:</strong> Real-time updates with WebSocket support</li>
            <li><strong>Database Integration:</strong> Load shots from sessions and players</li>
            <li><strong>Live Mode:</strong> Real-time shot monitoring and statistics</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Main Visualization Tabs */}
      <Tabs defaultValue="analytics" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="demo">Demo View</TabsTrigger>
          <TabsTrigger value="session">Session Data</TabsTrigger>
          <TabsTrigger value="live">Live Mode</TabsTrigger>
          <TabsTrigger value="monitor">Monitor</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Analytics Dashboard - Feature 3 Complete!</CardTitle>
              <CardDescription>
                Comprehensive shot analysis with wagon wheel, trends, and live data integration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                <AlertTitle>New Analytics Features</AlertTitle>
                <AlertDescription>
                  This dashboard demonstrates all the new analytics components:
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>• <strong>Wagon Wheel:</strong> Shot distribution on cricket field</div>
                    <div>• <strong>Historical Trends:</strong> Performance over time</div>
                    <div>• <strong>Multi-metric Analysis:</strong> Comprehensive charts</div>
                    <div>• <strong>Real-time Updates:</strong> Live data integration</div>
                  </div>
                </AlertDescription>
              </Alert>
              <EnhancedAnalyticsDashboard 
                sessionId={DEMO_SESSION_ID}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sample Data Demonstration</CardTitle>
              <CardDescription>
                Explore the visualization with pre-loaded sample cricket shots
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ShotVisualizationSystem />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="session" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Session-Based Data</CardTitle>
              <CardDescription>
                Load and visualize shots from a specific training session
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Info className="h-4 w-4" />
                <AlertTitle>Session Mode</AlertTitle>
                <AlertDescription>
                  This mode loads shots from a specific session ID. Use the data source selector to switch to "Session Data".
                </AlertDescription>
              </Alert>
              <ShotVisualizationSystem sessionId={DEMO_SESSION_ID} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="live" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Live Visualization (Real-time Updates)</CardTitle>
              <CardDescription>
                3D visualization with real-time WebSocket updates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Wifi className="h-4 w-4" />
                <AlertTitle>WebSocket Live Mode</AlertTitle>
                <AlertDescription>
                  The visualization connects via WebSocket for instant updates. 
                  Switch to "Live Mode" in the data source selector and use the "Simulate Shot" button to test.
                </AlertDescription>
              </Alert>
              <ShotVisualizationSystem sessionId={DEMO_SESSION_ID} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitor" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Live Monitoring Dashboard</CardTitle>
              <CardDescription>
                Real-time session monitoring with statistics and shot feed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LiveMonitor sessionId={DEMO_SESSION_ID} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
          <CardDescription>
            Upcoming features and enhancements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="mt-1 bg-green-100 text-green-800">✓</Badge>
              <div>
                <h4 className="font-semibold">Analytics Dashboard - COMPLETED!</h4>
                <p className="text-sm text-gray-600">
                  ✅ Wagon wheel field visualization, historical trends, enhanced dashboard with tabs and filters
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-1">4</Badge>
              <div>
                <h4 className="font-semibold">Develop Comparison Tool</h4>
                <p className="text-sm text-gray-600">
                  Enable multi-shot overlay visualization for technique comparison and improvement
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="mt-1">5</Badge>
              <div>
                <h4 className="font-semibold">Add Export Functionality</h4>
                <p className="text-sm text-gray-600">
                  Generate PDF reports with 3D visualizations and performance summaries
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShotVisualizationDemo;